<template>
  <b-card no-body>
    <b-card-header class="align-items-start">
      <h4>Sales & Targets</h4>
      <div class="flex-grow-1 ml-1">
        <b-spinner
          v-if="loading"
          small
          label="Small Spinner"
        />
      </div>
    </b-card-header>
    <b-card-body>
      <b-alert
        variant="danger"
        :show="loadingError ? true : false"
      >
        <div class="alert-body">
          <p>
            {{ loadingError }}
          </p>
        </div>
      </b-alert>
      <template v-if="!loadingError">
        <b-row>
          <b-col
            cols="12"
            md="3"
            class="position-relative"
          >
            <div style="display: flex; position: absolute; gap: 10px; top: 0; right: 12px;">
              <b-form-checkbox
                :checked="filtersData.buyer_type === 'cash'"
                @change="filtersData.buyer_type = filtersData.buyer_type === 'cash' ? '' : 'cash'"
              >
                Cash
              </b-form-checkbox>
              <b-form-checkbox
                :checked="filtersData.buyer_type === 'credit'"
                @change="filtersData.buyer_type = filtersData.buyer_type === 'credit' ? '' : 'credit'"
              >
                Credit
              </b-form-checkbox>
            </div>
            <b-form-group label="Buyers">
              <custom-v-select
                v-model="filtersData.buyer"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="buyerOptions"
                label="text"
                placeholder="Select Buyers"
                multiple
                :disabled="loading"
                :close-on-select="false"
                :reduce="data => data.id"
                :deselect-from-dropdown="true"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group label="Areas">
              <custom-v-select
                v-model="filtersData.area"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="areaOptions"
                label="text"
                placeholder="Select Areas"
                multiple
                :disabled="loading"
                :close-on-select="false"
                :reduce="data => data.id"
                :deselect-from-dropdown="true"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group
              label="Salesman"
              label-for="salesman"
            >
              <custom-v-select
                id="salesman"
                v-model="filtersData.salesmans"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="salesmanOptions"
                label="text"
                placeholder="Select Salesman"
                multiple
                :close-on-select="false"
                :deselect-from-dropdown="true"
                :reduce="option => option.id"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group
              label="Month Range"
              label-for="monthRange"
            >
              <flat-pickr
                id="monthRange"
                v-model="dateRange"
                placeholder="Select Month Range"
                :config="flatpickrConfig"
                @input="onDateChange"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            class="position-relative"
          >
            <div style="display: flex; position: absolute; gap: 10px; top: 0; right: 12px;">
              <b-form-checkbox
                :checked="filtersData.sort_order === 'asc'"
                @change="filtersData.sort_order = filtersData.sort_order === 'asc' ? 'desc' : 'asc'"
              >
                ASC
              </b-form-checkbox>
              <b-form-checkbox
                :checked="filtersData.sort_order === 'desc'"
                @change="filtersData.sort_order = filtersData.sort_order === 'desc' ? 'asc' : 'desc'"
              >
                DESC
              </b-form-checkbox>
            </div>
            <b-form-group
              label="Sort By"
              label-for="sortBy"
            >
              <custom-v-select
                id="sortBy"
                v-model="filtersData.sort_by"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sortByOptions"
                label="text"
                placeholder="Select Sort By"
                :reduce="option => option.value"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            class="position-relative"
          >
            <div style="display: flex; flex-direction: column; position: absolute; gap: 5px; top: 0; left: 12px;">
              <b-form-checkbox
                :checked="filtersData.has_current_month_target === 'false'"
                @change="filtersData.has_current_month_target = filtersData.has_current_month_target === 'false' ? '' : 'false'"
              >
                Pending for this month
              </b-form-checkbox>
              <b-form-checkbox
                :checked="filtersData.has_current_month_target === true"
                @change="filtersData.has_current_month_target = filtersData.has_current_month_target === true ? '' : true"
              >
                Has Target
              </b-form-checkbox>
            </div>
          </b-col>
        </b-row>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BCard, BSpinner, BCardHeader, BCardBody, BAlert,
  BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index'
import 'flatpickr/dist/plugins/monthSelect/style.css'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BCard,
    CustomVSelect,
    BSpinner,
    flatPickr,
    BCardHeader,
    BCardBody,
    BAlert,
    BFormCheckbox,
  },
  data() {
    return {
      salesmanOptions: [],
      buyerOptions: [],
      areaOptions: [],
      sortByOptions: [
        { text: 'Buyer Name', value: 'buyer_identifier' },
        { text: 'Total Sales', value: 'total_sales' },
        { text: 'Total Returns', value: 'total_returns' },
        { text: 'Net Sales', value: 'net_sales' },
      ],
      dateRange: '',
      filtersData: {
        salesmans: [],
        buyer: [],
        buyer_type: '',
        area: [],
        start_month: '',
        end_month: '',
        sort_by: 'buyer_identifier',
        sort_order: 'asc',
        has_current_month_target: '',
      },
      flatpickrConfig: {
        mode: 'range',
        dateFormat: 'Y-m',
        altInput: true,
        altFormat: 'F Y',
        plugins: [new MonthSelectPlugin({ shorthand: true, dateFormat: 'Y-m', altFormat: 'F Y' })],
      },
      loading: true,
      loadingError: null,
    }
  },
  watch: {
    filtersData: {
      deep: true,
      handler() {
        this.emitFiltersData()
      },
    },
  },
  async created() {
    this.setDefaultDates()
    await this.fetchOptions()
  },
  methods: {
    setDefaultDates() {
      const now = new Date()

      // Get previous month
      const endDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const endYear = endDate.getFullYear()
      const endMonth = endDate.getMonth() + 1

      // Get 6 months before end month
      const startDate = new Date(endYear, endMonth - 6, 1)
      const startYear = startDate.getFullYear()
      const startMonth = startDate.getMonth() + 1

      this.filtersData.end_month = `${endYear}-${String(endMonth).padStart(2, '0')}`
      this.filtersData.start_month = `${startYear}-${String(startMonth).padStart(2, '0')}`
      this.dateRange = `${this.filtersData.start_month} to ${this.filtersData.end_month}`
    },
    onDateChange(selectedDates) {
      if (selectedDates) {
        if (selectedDates.includes(' to ')) {
          // Range selection
          const [startDate, endDate] = selectedDates.split(' to ')
          this.filtersData.start_month = startDate.trim()
          this.filtersData.end_month = endDate.trim()
        } else {
          // Single month selection
          this.filtersData.start_month = selectedDates.trim()
          this.filtersData.end_month = selectedDates.trim()
        }
      }
    },
    async fetchOptions() {
      this.loading = true
      try {
        const [salesmanRes, buyersRes, areasRes] = await Promise.all([
          axios.get('/accounts/salesmans_options'),
          axios.get('/accounts/buyers_options'),
          axios.get('/accounts/areas_options'),
        ])

        this.salesmanOptions = salesmanRes.data.data
        this.buyerOptions = buyersRes.data.data
        this.areaOptions = areasRes.data.data
      } catch (error) {
        this.loadingError = error?.response?.data?.error || 'Error fetching filter options'
      }
      this.loading = false
    },
    emitFiltersData() {
      const filters = {
        start_month: this.filtersData.start_month,
        end_month: this.filtersData.end_month,
        sort_by: this.filtersData.sort_by,
        sort_order: this.filtersData.sort_order,
      }

      if (this.filtersData.salesmans.length > 0) {
        filters.salesmans = this.filtersData.salesmans.join(',')
      }
      if (this.filtersData.buyer.length > 0) {
        filters.buyer = this.filtersData.buyer.join(',')
      }
      if (this.filtersData.buyer_type) {
        filters.buyer_type = this.filtersData.buyer_type
      }
      if (this.filtersData.area.length > 0) {
        filters.area = this.filtersData.area.join(',')
      }
      if (this.filtersData.has_current_month_target) {
        filters.has_current_month_target = this.filtersData.has_current_month_target
      }

      this.$emit('filtersUpdated', filters)
    },
  },
}
</script>
